import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/lib/api";
import { toast } from "sonner";

function display(value) {
  return value === null || value === undefined ? "-" : value;
}

export default function TeacherClassDetail() {
  const { classId } = useParams();
  const [klass, setKlass] = useState(null);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [regenerating, setRegenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [classRes, rosterRes] = await Promise.all([
        api.get(`/teacher/classes/${classId}`),
        api.get(`/teacher/classes/${classId}/roster`),
      ]);
      setKlass(classRes.data);
      setRoster(rosterRes.data?.students || []);
      setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load class.");
    } finally {
      setLoading(false);
    }
  }, [classId]);

  useEffect(() => {
    load();
  }, [load]);

  const regenerateCode = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post(`/teacher/classes/${classId}/regenerate-code`);
      setKlass((k) => ({ ...k, ...data }));
      toast.success("Join code regenerated.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not regenerate code.");
    } finally {
      setRegenerating(false);
    }
  };

  const analytics = klass?.analytics || {};

  return (
    <div className="min-h-screen bg-forge-900 text-forge-100">
      <Header />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12 sm:py-16">
        <Link to="/teacher" className="text-xs uppercase tracking-[0.25em] text-forge-100/45 hover:text-ember-400">Back to Teacher</Link>
        {loading ? (
          <div className="mt-10 text-sm tracking-[0.3em] uppercase text-ember-500 animate-pulse">Loading class...</div>
        ) : error ? (
          <div className="mt-10 border border-red-500/30 bg-red-500/10 p-5 text-sm text-red-200">{error}</div>
        ) : (
          <>
            <div className="flex items-end justify-between gap-6 flex-wrap mt-10 mb-10">
              <div>
                <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-3">Class Detail</div>
                <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-2">{klass.name}</h1>
                <p className="text-sm text-forge-100/55">{klass.term || "No term"}</p>
              </div>
              <div className="border border-white/10 p-5 min-w-[220px]">
                <div className="text-[10px] uppercase tracking-[0.3em] text-forge-100/45 mb-2">Join Code</div>
                <div className="font-mono text-3xl text-ember-400 tracking-[0.18em]">{klass.join_code}</div>
                <button onClick={regenerateCode} disabled={regenerating} className="mt-4 text-xs uppercase tracking-[0.2em] text-forge-100/55 hover:text-gold-400 disabled:opacity-60">
                  {regenerating ? "Regenerating..." : "Regenerate code"}
                </button>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Stat label="Students" value={analytics.student_count ?? roster.length} />
              <Stat label="Readings" value={analytics.reading_completed_count ?? 0} />
              <Stat label="Debates" value={analytics.debate_completed_count ?? 0} />
              <Stat label="Journals" value={analytics.journal_completed_count ?? 0} />
            </div>
            <section className="border border-white/10 p-6 sm:p-8 mb-12">
              <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
                <div>
                  <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-2">Reasoning Outcomes</div>
                  <p className="text-sm text-forge-100/55">
                    Aggregated from {analytics.report_card_count ?? 0} completed Debate Mentor report card{(analytics.report_card_count ?? 0) === 1 ? "" : "s"}.
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-forge-100/45 mb-1">Average</div>
                  <div className="font-serif text-5xl text-ember-400">{display(analytics.avg_reasoning_score)}</div>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-px bg-white/5">
                <Reason label="Strongest Category" category={analytics.strongest_category} tone="ember" />
                <Reason label="Growth Edge" category={analytics.weakest_category} tone="gold" />
              </div>
            </section>

            <section>
              <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-4">Roster</div>
              {roster.length ? (
                <div className="overflow-x-auto border border-white/10">
                  <table className="w-full text-sm">
                    <thead className="bg-white/5 text-forge-100/50 uppercase tracking-[0.2em] text-[10px]">
                      <tr>
                        <th className="text-left p-4">Student</th>
                        <th className="text-right p-4">Read</th>
                        <th className="text-right p-4">Debate</th>
                        <th className="text-right p-4">Journal</th>
                        <th className="text-right p-4">Avg</th>
                        <th className="text-left p-4">Growth Edge</th>
                        <th className="text-left p-4">Last Activity</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {roster.map((student) => (
                        <tr key={student.user_id} className="hover:bg-white/[0.03]">
                          <td className="p-4">
                            <Link to={`/teacher/classes/${classId}/students/${student.user_id}`} className="font-serif text-lg hover:text-ember-400 transition-colors">
                              {student.name}
                            </Link>
                            <div className="text-xs text-forge-100/40">{student.email}</div>
                          </td>
                          <td className="p-4 text-right">{student.reading_completed_count}</td>
                          <td className="p-4 text-right">{student.debate_completed_count}</td>
                          <td className="p-4 text-right">{student.journal_completed_count}</td>
                          <td className="p-4 text-right">{display(student.avg_reasoning_score)}</td>
                          <td className="p-4">{student.weakest_category?.label || "-"}</td>
                          <td className="p-4 text-forge-100/55">{student.last_activity_at ? student.last_activity_at.slice(0, 10) : "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="border border-dashed border-white/10 p-10 text-center text-sm text-forge-100/55">
                  No students have joined yet. Share the join code with your class.
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-white/10 p-6">
      <div className="text-[10px] uppercase tracking-[0.3em] text-forge-100/45 mb-3">{label}</div>
      <div className="font-serif text-4xl text-forge-100">{value}</div>
    </div>
  );
}

function Reason({ label, category, tone }) {
  const color = tone === "gold" ? "text-gold-500" : "text-ember-500";
  return (
    <div className="bg-forge-900 p-5">
      <div className={`text-[10px] tracking-[0.3em] uppercase mb-2 ${color}`}>{label}</div>
      <div className="font-serif text-2xl text-forge-100">{category?.label || "-"}</div>
      <div className="text-xs text-forge-100/55 mt-1">{category ? `Avg ${category.avg} / 10` : "No completed report cards yet"}</div>
    </div>
  );
}
